<template>
    <div class="mx-auto flex max-w-xl flex-col items-center pt-40">
        <component
            :is="variant === 'page-not-found' ? SleepingCloud : ScreamingCloud"
            class="mb-2"
        />
        <h1 class="mb-1 text-heading-lg font-normal text-content-primary">{{ title }}</h1>
        <p
            v-if="variant === 'unknown'"
            class="ds-page-level-error-description"
        >
            We ran into an unexpected error while trying to load the page, please try again. If the problem continues, contact us
            at
            <a
                class="underline"
                href="mailto:support@closinglock.com"
            >
                support@closinglock.com</a
            >.
        </p>
        <p
            v-else-if="variant === 'unauthorized'"
            class="ds-page-level-error-description"
        >
            You don't have permission to view this page.
        </p>
        <p
            v-else-if="variant === 'page-not-found'"
            class="ds-page-level-error-description"
        >
            This page no longer exists or may have been deleted.
        </p>
        <div class="flex items-center gap-3">
            <slot name="secondaryAction">
                <DsButton
                    v-if="secondaryActionHref && secondaryActionTitle"
                    :href="secondaryActionHref"
                    size="sm"
                    variant="secondary"
                >
                    {{ secondaryActionTitle }}
                </DsButton>
            </slot>

            <slot name="primaryAction">
                <DsButton
                    v-if="primaryActionHref && primaryActionTitle"
                    :href="primaryActionHref ?? $route.fullPath"
                    size="sm"
                    variant="primary"
                >
                    {{ primaryActionTitle }}
                </DsButton>
            </slot>
        </div>
    </div>
</template>

<script setup lang="ts">
import ScreamingCloud from '@/assets/svg/screaming-cloud.svg?component';
import SleepingCloud from '@/assets/svg/sleeping-cloud.svg?component';
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import { computed } from 'vue';
import type { DsPageLevelErrorProps } from './page-level-error.types';

const props = defineProps<DsPageLevelErrorProps>();

const title = computed(() => {
    switch (props.variant) {
        case 'unknown':
            return 'Something went wrong';
        case 'unauthorized':
            return 'You no longer have access';
        case 'page-not-found':
            return 'Page not found';
        default:
            return 'Something went wrong';
    }
});
</script>

<style scoped lang="scss">
.ds-page-level-error-description {
    @apply mb-4 text-center text-content-lg text-content-primary;
}
</style>
